import React from 'react';
import PropTypes from 'prop-types';
import XRegExp from 'xregexp';

import { RenderNode } from 'libs/editor/plugins/helpers/renderNode';
import { FakeLink } from 'legacyComponents/styles';

import { patterns } from 'libs/editor/patterns';
import styled from 'styled-components';

// Extra functions brought from:
// - slate-react/src/components/leaf.js
// - slate-react/src/components/text.js

const renderText = props => {
  const { block, node, editor, parent, text, index, leaves } = props;
  // COMPAT: Render text inside void nodes with a zero-width space.
  // So the node can contain selection but the text is not visible.
  if (editor.query('isVoid', parent)) {
    return (
      <span data-slate-zero-width='z' data-slate-length={parent.text.length}>
        {'\uFEFF'}
      </span>
    );
  }

  // COMPAT: If this is the last text node in an empty block, render a zero-
  // width space that will convert into a line break when copying and pasting
  // to support expected plain text.
  if (
    text === '' &&
    parent.object === 'block' &&
    parent.text === '' &&
    parent.nodes.last() === node
  ) {
    return (
      <span data-slate-zero-width='n' data-slate-length={0}>
        {'\uFEFF'}
        <br />
      </span>
    );
  }

  // COMPAT: If the text is empty, it's because it's on the edge of an inline
  // node, so we render a zero-width space so that the selection can be
  // inserted next to it still.
  if (text === '') {
    return (
      <span data-slate-zero-width='z' data-slate-length={0}>
        {'\uFEFF'}
      </span>
    );
  }

  // COMPAT: Browsers will collapse trailing new lines at the end of blocks,
  // so we need to add an extra trailing new lines to prevent that.
  const lastText = block.getLastText();
  const lastChar = text.charAt(text.length - 1);
  // eslint-disable-next-line
  const isLastText = node == lastText;
  // eslint-disable-next-line
  const isLastLeaf = index == leaves.size - 1;
  const splitText = XRegExp.split(text, patterns.mention).filter(e => !!e);
  const output = splitText.map((t, i) => {
    const match = XRegExp.exec(t, patterns.mention);
    if (match && match.username && match.username.length > 1) {
      return (
        <FakeLink
          key={i}
          onClick={() => {
            window.location.href = `/${match[0]}`;
          }}
          mention
        >
          {t}
        </FakeLink>
      );
    }
    if (match && match.tag && match.tag.length > 1) {
      return (
        <FakeLink
          key={i}
          onClick={() => {
            window.location.href = `/topic/${match[0].substring(1)}`;
          }}
          mention
        >
          {t}
        </FakeLink>
      );
    }
    /* eslint-disable eqeqeq */
    if (
      i === splitText.length - 1 &&
      isLastText &&
      isLastLeaf &&
      lastChar == '\n'
    ) {
      return <React.Fragment key={i}>{`${t}\n`}</React.Fragment>;
    }
    return <React.Fragment key={i}>{t}</React.Fragment>;
  });
  return output;
};

const renderMarks = props => {
  const { marks, node, offset, text, editor } = props;
  const leaf = renderText(props);

  return marks.reduce((children, mark) => {
    // eslint-disable-next-line
    const props = {
      editor,
      mark,
      marks,
      node,
      offset,
      text,
      children,
    };
    const element = editor.run('renderMark', props);
    return element || children;
  }, leaf);
};

const renderLeaf = (leaves, leaf, index, offset, childProps) => {
  const { block, node, parent, editor } = childProps;
  const { text, marks } = leaf;
  const key = `${node.key}-${index}`;

  const props = {
    key,
    block,
    editor,
    index,
    marks,
    node,
    offset,
    parent,
    leaves,
    text,
  };

  return (
    <span key={key} data-offset-key={key}>
      {renderMarks(props)}
    </span>
  );
};

const ParagraphText = styled.div({
  wordBreak: 'break-word',
});

class ParagraphNode extends React.Component {
  render() {
    const { readOnly, attributes } = this.props;
    const children = !readOnly
      ? this.props.children
      : this.props.children.map(child => {
          if (child.props.node.object === 'text') {
            const leaves = child.props.node.getLeaves();
            let offset = 0;
            const leafChildren = leaves.map((leaf, i) => {
              const leafChild = renderLeaf(
                leaves,
                leaf,
                i,
                offset,
                child.props,
              );

              offset += leaf.text.length;
              return leafChild;
            });

            return (
              <span key={child.props.node.key} data-key={child.props.node.key}>
                {leafChildren}
              </span>
            );
          }
          return child;
        });

    return <ParagraphText {...attributes}>{children}</ParagraphText>;
  }
}

ParagraphNode.propTypes = {
  readOnly: PropTypes.bool,
  children: PropTypes.node,
  attributes: PropTypes.object,
};

export function Paragraph() {
  return RenderNode('paragraph', props => {
    return <ParagraphNode {...props} />;
  });
}
